import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type PublicUser = {
  id: string;
  email: string | null;
  emailVerified?: boolean;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
  phone?: string | null;
  country?: string | null;
  hasPassword?: boolean;
};

async function fetchMe(): Promise<PublicUser | null> {
  const res = await fetch("/api/user/me", { credentials: "include" });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error("Failed to fetch user");
  return res.json();
}

export function useUserAuth() {
  const qc = useQueryClient();

  const me = useQuery<PublicUser | null>({
    queryKey: ["/api/user/me"],
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  });

  const logout = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/user/logout", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Logout failed");
      return res.json();
    },
    onSuccess: () => {
      qc.setQueryData(["/api/user/me"], null);
    },
  });

  return {
    user: me.data ?? null,
    isLoading: me.isLoading,
    isAuthenticated: !!me.data,
    refetch: me.refetch,
    logout: logout.mutateAsync,
    isLoggingOut: logout.isPending,
  };
}
